// src/pages/logistica/ForecastImportar.jsx
//
// Requiere el paquete 'xlsx' (SheetJS) en el frontend:
//   npm install xlsx
//
// Parsea un Excel tipo pivote (renglón = artículo, columna = semana,
// agrupadas por mes con subtotal) y lo sube en formato largo
// (forecast_nombre, semana, cantidad) al backend.

import { useState } from 'react';
import * as XLSX from 'xlsx';
import api from '../../services/api';

/** Lunes de la semana ISO dada (algoritmo estándar) */
function mondayOfIsoWeek(week, year) {
  const jan4 = new Date(year, 0, 4);
  const jan4DayNum = (jan4.getDay() + 6) % 7; // Lunes=0 ... Domingo=6
  const mondayWeek1 = new Date(jan4);
  mondayWeek1.setDate(jan4.getDate() - jan4DayNum);
  const monday = new Date(mondayWeek1);
  monday.setDate(mondayWeek1.getDate() + (week - 1) * 7);
  return monday;
}

function toISODateString(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Parsea la matriz cruda (array de arrays) del Excel a formato largo.
 * Busca la fila de encabezado (la que tiene varias celdas "W##"),
 * identifica columnas de semana vs columnas de subtotal de mes, calcula
 * el año real de cada semana (con el mismo patrón de tu archivo:
 * subtotales tipo " 2026 OCT" agrupan semanas, y W01 después de W52
 * pertenece al año siguiente aunque esté agrupado bajo el mes anterior).
 */
function parsearPivote(matriz) {
  let filaEncabezado = -1;
  for (let i = 0; i < matriz.length; i++) {
    const fila = matriz[i] || [];
    const coincidencias = fila.filter((c) => typeof c === 'string' && /^\s*W\d+\s*$/i.test(c)).length;
    if (coincidencias >= 3) { filaEncabezado = i; break; }
  }
  if (filaEncabezado === -1) throw new Error('No se encontró la fila de encabezado con semanas (ej. "W41").');

  const encabezados = matriz[filaEncabezado];
  const columnasSemana = []; // { col, semanaNum, nominalYear }
  const columnasSubtotal = []; // { col, year }

  // La columna del nombre de artículo NO se asume por posición fija —
  // se busca por el texto "Etiquetas de fila" (Excel/pivote de Google
  // Sheets la nombra así). Si SheetJS recorta una columna vacía a la
  // izquierda (pasó con la columna A completamente vacía de este
  // archivo), asumir "columna 1" se desalinea con todo lo demás.
  let columnaNombre = encabezados.findIndex((v) => typeof v === 'string' && /etiquetas de fila/i.test(v));

  encabezados.forEach((valor, col) => {
    if (typeof valor !== 'string') return;
    const limpio = valor.trim();
    const matchSemana = limpio.match(/^W(\d{1,2})$/i);
    if (matchSemana) {
      columnasSemana.push({ col, semanaNum: parseInt(matchSemana[1]), nominalYear: null });
      return;
    }
    const matchAnio = limpio.match(/(\d{4})/);
    if (matchAnio && !matchSemana) {
      columnasSubtotal.push({ col, year: parseInt(matchAnio[1]) });
    }
  });

  if (columnasSemana.length === 0) throw new Error('No se detectaron columnas de semana.');
  // Respaldo si no se encontró el texto "Etiquetas de fila": usar la
  // columna justo antes de la primera semana detectada.
  if (columnaNombre === -1) columnaNombre = columnasSemana[0].col - 1;
  if (columnaNombre < 0) throw new Error('No se pudo determinar la columna del nombre de artículo.');

  // Año nominal de cada semana = año del subtotal de mes más cercano a la derecha.
  columnasSemana.forEach((c) => {
    const siguienteSubtotal = columnasSubtotal.find((s) => s.col > c.col);
    c.nominalYear = siguienteSubtotal ? siguienteSubtotal.year : columnasSubtotal[columnasSubtotal.length - 1]?.year;
  });

  // Año real: si el número de semana baja respecto a la anterior, hubo
  // vuelta de año (ej. W52 -> W01) aunque el subtotal siga diciendo el
  // mismo año — ese caso pertenece al año siguiente.
  let prevSemanaNum = null;
  let addAnio = 0;
  columnasSemana.forEach((c) => {
    if (prevSemanaNum !== null && c.semanaNum < prevSemanaNum) addAnio += 1;
    c.realYear = (c.nominalYear || new Date().getFullYear()) + addAnio;
    prevSemanaNum = c.semanaNum;
  });

  const filas = [];
  for (let i = filaEncabezado + 1; i < matriz.length; i++) {
    const fila = matriz[i];
    if (!fila) continue;
    const nombre = fila[columnaNombre];
    if (!nombre || typeof nombre !== 'string' || !nombre.trim()) continue;

    columnasSemana.forEach((c) => {
      const valor = fila[c.col];
      if (valor === undefined || valor === null || valor === '') return;
      const cantidad = parseFloat(valor);
      if (isNaN(cantidad)) return;
      const semana = toISODateString(mondayOfIsoWeek(c.semanaNum, c.realYear));
      filas.push({ forecast_nombre: nombre.trim(), semana, cantidad });
    });
  }

  return filas;
}

export default function ForecastImportar() {
  const [archivo, setArchivo] = useState(null);
  const [filasParseadas, setFilasParseadas] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  function handleArchivo(e) {
    const file = e.target.files[0];
    if (!file) return;
    setArchivo(file);
    setMensaje(null);
    setFilasParseadas(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const matriz = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
        const filas = parsearPivote(matriz);
        console.log('[DEBUG forecast] filas de encabezado detectada, primeras 2 filas de datos:', matriz.slice(0, 4));
        console.log('[DEBUG forecast] total filas parseadas:', filas.length, filas.slice(0, 5));
        setFilasParseadas(filas);
      } catch (err) {
        console.error(err);
        setMensaje({ tipo: 'error', texto: `No se pudo leer el archivo: ${err.message}` });
      }
    };
    reader.readAsArrayBuffer(file);
  }

  async function handleSubir() {
    if (!filasParseadas || filasParseadas.length === 0) return;
    try {
      setSubiendo(true);
      setMensaje(null);
      const res = await api.post('/api/logistica/forecast/importar', {
        archivoOrigen: archivo?.name,
        filas: filasParseadas,
      });
      setMensaje({
        tipo: 'ok',
        texto: `✓ ${res.data.filas_procesadas} filas importadas. ${res.data.pendientes_de_mapear} nombre(s) pendientes de mapear en "Mapeo de Forecast".`,
      });
      setFilasParseadas(null);
      setArchivo(null);
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo importar.' });
    } finally {
      setSubiendo(false);
    }
  }

  const articulosUnicos = filasParseadas ? new Set(filasParseadas.map((f) => f.forecast_nombre)).size : 0;
  const semanasUnicas = filasParseadas ? new Set(filasParseadas.map((f) => f.semana)).size : 0;

  return (
    <div style={styles.contenedor}>
      <h1 style={styles.titulo}>Importar Forecast</h1>
      <p style={styles.subtitulo}>Sube el Excel trimestral de planeación (formato pivote: artículo por renglón, semana por columna).</p>

      <div style={styles.tarjeta}>
        <input type="file" accept=".xlsx,.xls" onChange={handleArchivo} style={styles.inputArchivo} />

        {filasParseadas && (
          <div style={styles.previewBox}>
            <div>✓ Archivo leído: <strong>{filasParseadas.length}</strong> celdas de dato, <strong>{articulosUnicos}</strong> renglones distintos, <strong>{semanasUnicas}</strong> semanas.</div>
            <button onClick={handleSubir} disabled={subiendo} style={styles.botonSubir}>
              {subiendo ? 'Importando...' : 'Confirmar e importar'}
            </button>
          </div>
        )}

        {mensaje && <div style={{ ...styles.mensaje, color: mensaje.tipo === 'ok' ? '#3E7C5A' : '#B23A48' }}>{mensaje.texto}</div>}
      </div>

      <div style={styles.avisoInfo}>
        Después de importar, entra a <strong>Mapeo de Forecast</strong> para asignar cada nombre nuevo (o subtotal como "GRANOLA"/"Total general") a su artículo real, o marcarlo como "ignorar". Solo hace falta una vez por nombre — no se vuelve a preguntar aunque cambien las columnas de semana en el próximo archivo.
      </div>
    </div>
  );
}

const styles = {
  contenedor: { fontFamily: 'Inter, sans-serif', padding: '24px', color: '#3A2E42', maxWidth: '640px' },
  titulo: { fontFamily: 'Playfair Display, serif', color: '#2A1830', marginBottom: '4px' },
  subtitulo: { fontSize: '12px', color: '#8A7690', marginBottom: '18px' },
  tarjeta: { background: '#fff', border: '1px solid #E6DDD2', borderRadius: '12px', padding: '18px', boxShadow: '0 2px 14px rgba(74,30,92,0.07)' },
  inputArchivo: { fontSize: '13px' },
  previewBox: { marginTop: '16px', padding: '12px', background: '#F5F3F0', borderRadius: '8px', fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '10px' },
  botonSubir: { padding: '9px 14px', fontSize: '13px', fontWeight: 600, color: '#fff', background: '#6A2C75', border: 'none', borderRadius: '8px', cursor: 'pointer', alignSelf: 'flex-start' },
  mensaje: { fontSize: '13px', marginTop: '14px', fontWeight: 600 },
  avisoInfo: { fontSize: '11.5px', color: '#6E4F80', background: '#EFE9F3', border: '1px solid #A98BB8', borderRadius: '8px', padding: '10px 14px', marginTop: '16px' },
};